<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useOvvaBackend } from 'dashboard/composables/useOvvaBackend';

import SourceFormDialog from './SourceFormDialog.vue';
import ChannelsTab from './ChannelsTab.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const { callBackend } = useOvvaBackend();

const isFetching = ref(true);
const isSaving = ref(false);
const deletingId = ref(null);
const provisioningId = ref(null);
const sources = ref([]);

const showFormDialog = ref(false);
const showDeleteDialog = ref(false);
const selectedSource = ref(null);

// Вкладка «Бренды» (0, старая таблица источников — поведение не менялось)
// и «Каналы» (1, новая — см. ChannelsTab.vue). SettingsLayout получает
// loading/empty-state только для активной вкладки «Бренды»: у «Каналов»
// своя внутренняя загрузка (двойной замок), к списку источников она
// отношения не имеет.
const selectedTab = ref(0);
const tabs = computed(() => [
  { key: 0, label: t('SOURCE_MGMT.TABS.BRANDS') },
  { key: 1, label: t('SOURCE_MGMT.TABS.CHANNELS') },
]);
const onClickTabChange = tab => {
  selectedTab.value = tab.key;
};
const layoutIsLoading = computed(
  () => selectedTab.value === 0 && isFetching.value
);
const layoutNoRecordsFound = computed(
  () => selectedTab.value === 0 && !isFetching.value && !sources.value.length
);

const TONE_LABEL_KEYS = {
  neutral: 'SOURCE_MGMT.FORM.TONE.NEUTRAL',
  friendly: 'SOURCE_MGMT.FORM.TONE.FRIENDLY',
  formal: 'SOURCE_MGMT.FORM.TONE.FORMAL',
};
const toneLabel = tone =>
  t(TONE_LABEL_KEYS[tone] || 'SOURCE_MGMT.FORM.TONE.DEFAULT');

const deleteMessage = computed(() => ` ${selectedSource.value?.name || ''}?`);

const tableHeaders = computed(() => [
  t('SOURCE_MGMT.LIST.TABLE_HEADER.NAME'),
  t('SOURCE_MGMT.LIST.TABLE_HEADER.WEBSITE'),
  t('SOURCE_MGMT.LIST.TABLE_HEADER.TONE'),
  t('SOURCE_MGMT.LIST.TABLE_HEADER.WIDGET'),
  t('SOURCE_MGMT.LIST.TABLE_HEADER.STATUS'),
  t('SOURCE_MGMT.LIST.TABLE_HEADER.ACTION'),
]);

const fetchSources = async () => {
  isFetching.value = true;
  try {
    const data = await callBackend('/admin/sources');
    sources.value = Array.isArray(data) ? data : [];
  } catch (error) {
    useAlert(error.message || t('SOURCE_MGMT.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const openAddDialog = () => {
  selectedSource.value = null;
  showFormDialog.value = true;
};

const openEditDialog = source => {
  selectedSource.value = source;
  showFormDialog.value = true;
};

const closeFormDialog = () => {
  showFormDialog.value = false;
};

const saveSource = async payload => {
  isSaving.value = true;
  try {
    const id = selectedSource.value?.id;
    await callBackend(id ? `/admin/sources/${id}` : '/admin/sources', {
      method: id ? 'PATCH' : 'POST',
      body: payload,
    });
    useAlert(
      id
        ? t('SOURCE_MGMT.EDIT.API.SUCCESS_MESSAGE')
        : t('SOURCE_MGMT.ADD.API.SUCCESS_MESSAGE')
    );
    closeFormDialog();
    fetchSources();
  } catch (error) {
    useAlert(error.message || t('SOURCE_MGMT.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

const openDeleteDialog = source => {
  selectedSource.value = source;
  showDeleteDialog.value = true;
};

const closeDeleteDialog = () => {
  showDeleteDialog.value = false;
};

const confirmDelete = async () => {
  const source = selectedSource.value;
  closeDeleteDialog();
  if (!source) return;
  deletingId.value = source.id;
  try {
    await callBackend(`/admin/sources/${source.id}`, { method: 'DELETE' });
    useAlert(t('SOURCE_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    fetchSources();
  } catch (error) {
    useAlert(error.message || t('SOURCE_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    deletingId.value = null;
  }
};

const provisionWidget = async source => {
  provisioningId.value = source.id;
  try {
    const data = await callBackend(
      `/admin/sources/${source.id}/provision-widget`,
      { method: 'POST' }
    );
    useAlert(
      data?.status === 'exists'
        ? t('SOURCE_MGMT.WIDGET.ALREADY_EXISTS')
        : t('SOURCE_MGMT.WIDGET.SUCCESS_MESSAGE')
    );
    fetchSources();
  } catch (error) {
    useAlert(error.message || t('SOURCE_MGMT.WIDGET.ERROR_MESSAGE'));
  } finally {
    provisioningId.value = null;
  }
};

onBeforeMount(fetchSources);
</script>

<template>
  <SettingsLayout
    :is-loading="layoutIsLoading"
    :loading-message="$t('SOURCE_MGMT.LOADING')"
    :no-records-found="layoutNoRecordsFound"
    :no-records-message="$t('SOURCE_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SOURCE_MGMT.HEADER')"
        :description="$t('SOURCE_MGMT.DESCRIPTION')"
      >
        <template #tabs>
          <TabBar
            :tabs="tabs"
            :initial-active-tab="selectedTab"
            @tab-changed="onClickTabChange"
          />
        </template>
        <template v-if="selectedTab === 0 && sources.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('SOURCE_MGMT.COUNT', { n: sources.length }) }}
          </span>
        </template>
        <template v-if="selectedTab === 0" #actions>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-refresh-cw"
            :label="$t('SOURCE_MGMT.REFRESH')"
            :is-loading="isFetching"
            @click="fetchSources"
          />
          <Button
            size="sm"
            icon="i-lucide-plus"
            :label="$t('SOURCE_MGMT.HEADER_BTN_TXT')"
            @click="openAddDialog"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <ChannelsTab v-if="selectedTab === 1" :sources="sources" />
      <BaseTable
        v-else
        :headers="tableHeaders"
        :items="sources"
        :no-data-message="$t('SOURCE_MGMT.LIST.404')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="source in items" :key="source.id" :item="source">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ source.name }}
                </span>
                <div
                  v-if="source.code"
                  class="text-label-small text-n-slate-11"
                >
                  {{ source.code }}
                </div>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ source.website || '—' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ toneLabel(source.tone) }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{
                    source.cwWebsiteToken
                      ? $t('SOURCE_MGMT.WIDGET.OWN', {
                          id: source.cwWidgetInboxId,
                        })
                      : $t('SOURCE_MGMT.WIDGET.SHARED')
                  }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-label-small"
                  :class="
                    source.isActive
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{
                    source.isActive
                      ? $t('SOURCE_MGMT.STATUS.ACTIVE')
                      : $t('SOURCE_MGMT.STATUS.INACTIVE')
                  }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-if="!source.cwWebsiteToken"
                    v-tooltip.top="$t('SOURCE_MGMT.WIDGET.CREATE')"
                    icon="i-lucide-globe"
                    slate
                    sm
                    :is-loading="provisioningId === source.id"
                    @click="provisionWidget(source)"
                  />
                  <Button
                    v-tooltip.top="$t('SOURCE_MGMT.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEditDialog(source)"
                  />
                  <Button
                    v-tooltip.top="$t('SOURCE_MGMT.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="deletingId === source.id"
                    @click="openDeleteDialog(source)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showFormDialog" :on-close="closeFormDialog">
      <SourceFormDialog
        :source="selectedSource"
        :is-saving="isSaving"
        @submit="saveSource"
        @close="closeFormDialog"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteDialog"
      :on-close="closeDeleteDialog"
      :on-confirm="confirmDelete"
      :title="$t('SOURCE_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('SOURCE_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('SOURCE_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('SOURCE_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
